import type { LucideIcon } from "lucide-react";
import { settingHash } from "./setting-anchors.ts";

/** One setting named in a summary line: a link to its row, with its value in words. */
export interface SummaryItem {
  key: string;
  label: string;
  value: string;
}

/** A summary line: an icon, its lead ("Set at this level:") and the settings it names. */
export interface SummaryLine {
  id: string;
  icon: LucideIcon;
  lead: string;
  items: readonly SummaryItem[];
}

const iconClass = "size-3.5 shrink-0 translate-y-0.5 text-fg-muted";

/**
 * What a group page holds, in a line or two (every settings screen):
 * each non-empty line names its settings, each a link to its row with its value; with no line
 * to show, the `empty` line says so.
 */
export function SummaryLines(props: {
  lines: readonly SummaryLine[];
  empty: { icon: LucideIcon; text: string };
  onReveal: (key: string) => void;
}) {
  const lines = props.lines.filter((line) => line.items.length > 0);
  const Empty = props.empty.icon;
  const item = (s: SummaryItem, i: number) => (
    <span key={s.key}>
      {i > 0 ? ", " : null}
      <a
        href={settingHash(s.key)}
        className="font-medium text-fg underline decoration-line-strong underline-offset-4 hover:decoration-fg"
        onClick={(e) => {
          // A new tab or window opens the anchor itself.
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
            return;
          }
          e.preventDefault();
          props.onReveal(s.key);
        }}
      >
        {s.label}
      </a>{" "}
      <span className="text-fg-secondary">({s.value})</span>
    </span>
  );
  return (
    <div className="flex flex-col gap-1.5 text-sm">
      {lines.map((line) => {
        const Icon = line.icon;
        return (
          <p key={line.id} className="flex items-baseline gap-2">
            <Icon aria-hidden="true" className={iconClass} strokeWidth={1.75} />
            <span>
              <span className="text-fg-secondary">{line.lead} </span>
              {line.items.map(item)}
            </span>
          </p>
        );
      })}
      {lines.length === 0 ? (
        <p className="flex items-baseline gap-2 text-fg-secondary">
          <Empty aria-hidden="true" className={iconClass} strokeWidth={1.75} />
          {props.empty.text}
        </p>
      ) : null}
    </div>
  );
}
